import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { useLocation, withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { useConfiguration } from '../../context/configurationContext';
import { IconSearch, IconSpinner, LayoutSingleColumn, NamedLink, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { getImagesLink } from '../../util/dataExtractors';
import heroImg from '../../assets/blog-hub/hero.webp';
import typeDump from '../../assets/blog-hub/type-dump.webp';
import typeUtility from '../../assets/blog-hub/type-utility.webp';
import typeCarHauler from '../../assets/blog-hub/type-car-hauler.jpg';
import typeEnclosed from '../../assets/blog-hub/type-enclosed.webp';
import typeEquipment from '../../assets/blog-hub/type-equipment.jpg';
import typeFlatbed from '../../assets/blog-hub/type-flatbed.webp';
import typeGooseneck from '../../assets/blog-hub/type-gooseneck.jpg';
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import css from './BlogPage.module.css';
import BlogCard from './BlogCard/BlogCard';
import {
  fetchAllBlogs,
  getBlogsInProgress,
  getBlogsSelector,
} from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';
import routeConfiguration from '../../routing/routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { createSlug, parse } from '../../util/urlHelpers';
import classNames from 'classnames';

const PAGE_SIZE = 12;
const strapiBaseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

// "Find a Trailer Near You" tiles — real trailer-type photos, linking into the
// marketplace via keyword search (works regardless of hosted pub_ field
// structure; 200-verified).
const TRAILER_TYPES = [
  { label: 'Dump', keywords: 'dump trailer', img: typeDump },
  { label: 'Utility', keywords: 'utility trailer', img: typeUtility },
  { label: 'Car Hauler', keywords: 'car hauler', img: typeCarHauler },
  { label: 'Enclosed', keywords: 'enclosed trailer', img: typeEnclosed },
  { label: 'Equipment', keywords: 'equipment trailer', img: typeEquipment },
  { label: 'Gooseneck', keywords: 'gooseneck trailer', img: typeGooseneck },
  { label: 'Flatbed', keywords: 'flatbed trailer', img: typeFlatbed },
];

// "Topic Hubs" — map to the existing category filter (?category=slug).
const TOPIC_HUBS = [
  { label: 'Towing Laws & Regulations', match: 'trailer-towing-laws-and-regulations' },
  { label: 'How-To Guides', match: 'how-to' },
  { label: 'Dump Trailers', match: 'dump-trailers' },
  { label: 'Flatbed Trailers', match: 'flatbed-trailers' },
  { label: 'Utility Trailers', match: 'utility-trailers' },
  { label: 'Enclosed Trailers', match: 'enclosed-trailers' },
];

// Curated, ordered category tabs. Keyed by the createSlug() of the Strapi tag
// name so URLs stay clean (?category=trailer-towing-laws-and-regulations). The
// "All" tab is prepended in the component.
const CATEGORY_TABS = [
  { label: 'Towing Laws', match: 'trailer-towing-laws-and-regulations' },
  { label: 'How-To Guides', match: 'how-to' },
  { label: 'Flatbed', match: 'flatbed-trailers' },
  { label: 'Utility', match: 'utility-trailers' },
  { label: 'Dump', match: 'dump-trailers' },
  { label: 'Enclosed', match: 'enclosed-trailers' },
  { label: 'Car Hauler', match: 'car-hauler-trailer' },
  { label: 'Gooseneck', match: 'gooseneck-trailers' },
  { label: 'Equipment', match: 'equipment-trailers' },
];

const getBlogTagSlug = blog => {
  const blogData = blog?.attributes || blog;
  const tag =
    blogData?.tag?.data?.attributes?.name ||
    blogData?.tag?.name ||
    blogData?.tag?.attributes?.name ||
    null;
  return tag ? createSlug(tag) : null;
};

export const BlogPageComponent = props => {
  const { intl, scrollingDisabled, history, viewport, params } = props;

  const config = useConfiguration();
  const dispatch = useDispatch();
  const location = useLocation();

  const search = parse(location.search);

  // Active category comes from ?category= (kept across pagination).
  const rawCategory = Array.isArray(search?.category) ? search?.category[0] : search?.category;
  const activeCategory = rawCategory ? createSlug(rawCategory) : null;

  // Current page comes from the /blog/page/:page route param. Clamp to >= 1.
  const pageParam = parseInt(params?.page, 10);
  const currentPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const [searchKeyword, setSearchKeyword] = useState('');

  useEffect(() => {
    // Data is loaded server-side via loadData for the crawlable path; this
    // client fetch keeps client-side navigation fresh.
    dispatch(fetchAllBlogs({}));
  }, []);

  const blogsData = useSelector(getBlogsSelector) || [];
  const searchInProgress = useSelector(getBlogsInProgress);

  // 1) Filter by active category.
  const categoryFiltered = activeCategory
    ? blogsData.filter(b => getBlogTagSlug(b) === activeCategory)
    : blogsData;

  // 2) Filter by keyword search (title match).
  const keywordFiltered = searchKeyword
    ? categoryFiltered.filter(b => {
        const blogData = b?.attributes || b;
        return (blogData?.title || '').toLowerCase().includes(searchKeyword.toLowerCase());
      })
    : categoryFiltered;

  // 3) Sort most-recent-first.
  const sortedBlogs = [...keywordFiltered].sort(
    (a, b) =>
      new Date((b?.attributes || b)?.createdAt) - new Date((a?.attributes || a)?.createdAt)
  );

  // 4) Paginate (only when not searching — search shows all matches).
  const totalPages = Math.max(1, Math.ceil(sortedBlogs.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedBlogs = searchKeyword
    ? sortedBlogs
    : sortedBlogs.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearchChange = event => setSearchKeyword(event.target.value);

  // Build a route/link to a given page + category, preserving category.
  const linkToPage = pageNum => {
    const queryParams = activeCategory ? { category: activeCategory } : {};
    if (pageNum <= 1) {
      return createResourceLocatorString('BlogPage', routeConfiguration(), {}, queryParams);
    }
    return createResourceLocatorString(
      'BlogPagePaginated',
      routeConfiguration(),
      { page: pageNum },
      queryParams
    );
  };

  const onCategoryClick = matchSlug => {
    // Switching category always resets to page 1.
    const queryParams = matchSlug ? { category: matchSlug } : {};
    history.push(
      createResourceLocatorString('BlogPage', routeConfiguration(), {}, queryParams)
    );
  };

  const isMobileLayout = viewport.width > 0 && viewport.width < 768;

  // ---- SEO meta (unique per page + category) ----
  const siteTitle = config.marketplaceName || 'BigLoads';
  const rootURL = config.marketplaceRootURL || '';

  const activeTab = CATEGORY_TABS.find(t => t.match === activeCategory);
  const categoryLabel = activeTab ? activeTab.label : null;

  const baseTitle = categoryLabel
    ? `${categoryLabel} — Trailer Guides & Towing Laws | ${siteTitle}`
    : `Trailer Rental Guides, Towing Laws & Hauling Know-How | ${siteTitle}`;
  // Suppress the "Page N" suffix while searching (pagination is collapsed).
  const showPageSuffix = !searchKeyword && safePage > 1;
  const pageTitle = showPageSuffix ? `${baseTitle} — Page ${safePage}` : baseTitle;

  const baseDescription = categoryLabel
    ? `${categoryLabel} from BigLoads — no-BS, straight-shooting advice on trailers, towing, and hauling heavy across America.`
    : `State-by-state towing laws, trailer rental breakdowns, and no-BS hauling advice from folks who actually pull heavy. Welcome to the BigLoads blog.`;
  const schemaDescription = showPageSuffix
    ? `${baseDescription} (Page ${safePage} of ${totalPages}.)`
    : baseDescription;

  // Self-referencing canonical for THIS page/category (so page 2 isn't seen as
  // a duplicate of page 1). Path-based pages get their own canonical.
  // While the user is doing a client-side keyword search, pagination is
  // bypassed (all matches shown), so the canonical must collapse to the
  // page-1 URL of the current category to avoid a page-2 canonical pointing at
  // an unpaginated result set (duplicate-content risk flagged in review).
  const canonicalPath = (() => {
    const catQuery = activeCategory ? `?category=${activeCategory}` : '';
    if (!searchKeyword && safePage > 1) return `/blog/page/${safePage}${catQuery}`;
    return `/blog${catQuery}`;
  })();
  const canonicalUrl = `${rootURL}${canonicalPath}`;

  const schemaImage = `${rootURL}${facebookImage}`;

  // The full content-hub experience (dual-intent split, featured grid,
  // browse-by-type, topic hubs, newsletter) renders ONLY on the true landing
  // state: /blog, page 1, no category, no keyword search. Paginated / category
  // / search views keep the clean grid so canonicals + UX stay correct.
  // Number-normalize the page compare so SSR (number) and client (possibly
  // string) agree — avoids a hydration mismatch that would flip the hub on/off.
  const isHubView = !activeCategory && !searchKeyword && Number(safePage) === 1;

  // Featured set = newest article (large) + next 4 (small cards). Only keep
  // entries that have a slug so we never build a /blog/undefined link.
  const withSlug = b => !!(b?.attributes || b)?.slug;
  const featuredMain = isHubView && withSlug(sortedBlogs[0]) ? sortedBlogs[0] : null;
  const featuredRest = isHubView ? sortedBlogs.slice(1, 5).filter(withSlug) : [];

  const blogFields = b => {
    const d = b?.attributes || b || {};
    const img = d?.coverImage?.url || d?.coverImage?.data?.attributes?.url || null;
    const imgs = getImagesLink([img], strapiBaseUrl);
    return {
      slug: d?.slug,
      title: d?.title,
      tag:
        d?.tag?.data?.attributes?.name || d?.tag?.name || d?.tag?.attributes?.name || null,
      image: isArrayLength(imgs) ? imgs[0] : null,
    };
  };

  // Numbered pagination window (compact): 1 … p-1 p p+1 … N
  const pageNumbers = [];
  if (!searchKeyword && totalPages > 1) {
    const windowSize = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= safePage - windowSize && i <= safePage + windowSize)
      ) {
        pageNumbers.push(i);
      } else if (
        pageNumbers[pageNumbers.length - 1] !== '…'
      ) {
        pageNumbers.push('…');
      }
    }
  }

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={pageTitle}
      canonicalUrl={canonicalUrl}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${rootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Blog',
        description: schemaDescription,
        name: pageTitle,
        url: canonicalUrl,
        image: [schemaImage],
        publisher: {
          '@type': 'Organization',
          name: siteTitle,
          url: rootURL,
        },
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          {/* ================= HERO ================= */}
          <section
            className={css.hero}
            style={{ backgroundImage: `url(${heroImg})` }}
          >
            <div className={css.heroOverlay} />
            <div className={css.heroInner}>
              <span className={css.heroEyebrow}>The BigLoads Garage</span>
              <h1 className={css.heroTitle}>
                Towing Laws, Rental Guides &amp; Straight-Up Hauling Know-How
              </h1>
              <p className={css.heroSubtitle}>
                State-by-state towing laws, trailer rental breakdowns, and no-BS advice from folks
                who actually pull heavy. Back your rig up, grab a coffee, and get smarter about
                hauling.
              </p>
              <div className={css.heroSearchForm}>
                <IconSearch />
                <input
                  type="text"
                  placeholder="Search the garage… (towing laws, flatbeds, hitches)"
                  value={searchKeyword}
                  onChange={handleSearchChange}
                  aria-label="Search blog articles"
                />
              </div>
            </div>
          </section>

          <div className={css.blogsContainer}>
            <div className={css.blogsContent}>
              {/* ================= CATEGORY TABS ================= */}
              <nav className={css.categoryNav} aria-label="Blog categories">
                <button
                  type="button"
                  className={classNames(css.categoryTab, { [css.categoryActive]: !activeCategory })}
                  onClick={() => onCategoryClick(null)}
                >
                  All Articles
                </button>
                {CATEGORY_TABS.map(tab => (
                  <button
                    type="button"
                    key={tab.match}
                    className={classNames(css.categoryTab, {
                      [css.categoryActive]: activeCategory === tab.match,
                    })}
                    onClick={() => onCategoryClick(tab.match)}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>

              {/* Crawlable category links for SEO (server-rendered <a> tags). */}
              <div className={css.categorySeoLinks} aria-hidden="false">
                {CATEGORY_TABS.map(tab => (
                  <NamedLink
                    key={`seo-${tab.match}`}
                    name="BlogPage"
                    to={{ search: `?category=${tab.match}` }}
                  >
                    {tab.label}
                  </NamedLink>
                ))}
              </div>

              {/* ============ FEATURED FROM THE GARAGE (hub only) ============ */}
              {isHubView && featuredMain ? (
                <section className={css.featuredSection}>
                  <h2 className={css.featuredTitle}>Featured From The Garage</h2>
                  <div className={css.featuredGrid}>
                    {(() => {
                      const f = blogFields(featuredMain);
                      return (
                        <NamedLink
                          name="SingleBlogPage"
                          params={{ title: f.slug }}
                          className={css.featuredMain}
                        >
                          {f.image ? (
                            <div className={css.featuredMainImg}>
                              <img src={f.image} alt={f.title} />
                            </div>
                          ) : null}
                          <div className={css.featuredMainBody}>
                            {f.tag ? <span className={css.cardEyebrow}>{f.tag}</span> : null}
                            <h3 className={css.featuredMainHeadline}>{f.title}</h3>
                          </div>
                        </NamedLink>
                      );
                    })()}
                    <div className={css.featuredSide}>
                      {featuredRest.map((b, i) => {
                        const f = blogFields(b);
                        return (
                          <NamedLink
                            key={f.slug || i}
                            name="SingleBlogPage"
                            params={{ title: f.slug }}
                            className={css.featuredSmall}
                          >
                            {f.image ? (
                              <div className={css.featuredSmallImg}>
                                <img src={f.image} alt={f.title} />
                              </div>
                            ) : null}
                            <div className={css.featuredSmallBody}>
                              {f.tag ? <span className={css.cardEyebrow}>{f.tag}</span> : null}
                              <span className={css.featuredSmallHeadline}>{f.title}</span>
                            </div>
                          </NamedLink>
                        );
                      })}
                    </div>
                  </div>
                </section>
              ) : null}

              {/* ============ FIND A TRAILER NEAR YOU (hub only) ============ */}
              {isHubView ? (
                <section className={css.browseSection}>
                  <h2 className={css.browseTitle}>Find a Trailer Near You</h2>
                  <div className={css.typeGrid}>
                    {TRAILER_TYPES.map(t => (
                      <NamedLink
                        key={t.label}
                        name="SearchPage"
                        to={{ search: `?keywords=${encodeURIComponent(t.keywords)}` }}
                        className={css.typeTile}
                      >
                        <div className={css.typeTileImg}>
                          <img src={t.img} alt={`${t.label} trailer rentals`} loading="lazy" />
                        </div>
                        <span className={css.typeLabel}>{t.label}</span>
                      </NamedLink>
                    ))}
                  </div>
                </section>
              ) : null}

              {categoryLabel ? (
                <h2 className={css.sectionHeading}>{categoryLabel}</h2>
              ) : (
                <h2 className={css.sectionHeading}>Latest From The Garage</h2>
              )}

              {/* ================= BLOG GRID ================= */}
              <div className={css.mainBlogContent}>
                {searchInProgress && !isArrayLength(blogsData) ? (
                  <IconSpinner />
                ) : isArrayLength(paginatedBlogs) ? (
                  paginatedBlogs.map((blog, index) => (
                    <BlogCard
                      key={blog?.id || index}
                      blog={blog}
                      showExcerpt={true}
                      showDate={false}
                    />
                  ))
                ) : (
                  <p className={css.noResults}>
                    No articles found{searchKeyword ? ` for "${searchKeyword}"` : ''}. Try another
                    search or category.
                  </p>
                )}
              </div>

              {/* ================= PAGINATION ================= */}
              {!searchKeyword && totalPages > 1 ? (
                <nav className={css.pagination} aria-label="Blog pagination">
                  {safePage > 1 ? (
                    <NamedLink
                      className={css.pageArrow}
                      name={safePage - 1 <= 1 ? 'BlogPage' : 'BlogPagePaginated'}
                      params={safePage - 1 <= 1 ? {} : { page: safePage - 1 }}
                      to={activeCategory ? { search: `?category=${activeCategory}` } : {}}
                    >
                      ‹ Prev
                    </NamedLink>
                  ) : (
                    <span className={classNames(css.pageArrow, css.pageDisabled)}>‹ Prev</span>
                  )}

                  {pageNumbers.map((n, i) =>
                    n === '…' ? (
                      <span key={`ellipsis-${i}`} className={css.pageEllipsis}>
                        …
                      </span>
                    ) : n === safePage ? (
                      <span
                        key={n}
                        className={classNames(css.pageNum, css.pageNumActive)}
                        aria-current="page"
                      >
                        {n}
                      </span>
                    ) : (
                      <NamedLink
                        key={n}
                        className={css.pageNum}
                        name={n <= 1 ? 'BlogPage' : 'BlogPagePaginated'}
                        params={n <= 1 ? {} : { page: n }}
                        to={activeCategory ? { search: `?category=${activeCategory}` } : {}}
                      >
                        {n}
                      </NamedLink>
                    )
                  )}

                  {safePage < totalPages ? (
                    <NamedLink
                      className={css.pageArrow}
                      name="BlogPagePaginated"
                      params={{ page: safePage + 1 }}
                      to={activeCategory ? { search: `?category=${activeCategory}` } : {}}
                    >
                      Next ›
                    </NamedLink>
                  ) : (
                    <span className={classNames(css.pageArrow, css.pageDisabled)}>Next ›</span>
                  )}
                </nav>
              ) : null}
            </div>
          </div>

          {/* ============ TOPIC HUBS (hub only) ============ */}
          {isHubView ? (
            <section className={css.topicSection}>
              <div className={css.topicInner}>
                <h2 className={css.topicTitle}>Explore Topic Hubs</h2>
                <div className={css.topicGrid}>
                  {TOPIC_HUBS.map(t => (
                    <NamedLink
                      key={t.match}
                      name="BlogPage"
                      to={{ search: `?category=${t.match}` }}
                      className={css.topicPill}
                    >
                      <span className={css.topicEmoji}>{t.emoji}</span>
                      <span>{t.label}</span>
                    </NamedLink>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {/* ============ NEWSLETTER — THE LOAD REPORT (hub only) ============ */}
          {isHubView ? <LoadReportSignup /> : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

// ---------------------------------------------------------------------------
// The Load Report — newsletter capture band.
// Posts to /api/newsletter/subscribe (server endpoint stubbed for now; wire to
// Mailchimp BigLoads list next). Fully client-side stateful; degrades to a
// mailto if the endpoint isn't available yet.
// ---------------------------------------------------------------------------
const LoadReportSignup = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | ok | error
  const [message, setMessage] = useState('');

  const onSubmit = async e => {
    e.preventDefault();
    const value = (email || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setStatus('error');
      setMessage('Enter a valid email so we can send the goods.');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, list: 'bigloads-load-report' }),
      });
      if (res.ok) {
        setStatus('ok');
        setMessage("You're in. Watch your inbox for The Load Report. 🦅");
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something jammed up. Try again in a sec.');
      }
    } catch (err) {
      setStatus('error');
      setMessage('Something jammed up. Try again in a sec.');
    }
  };

  return (
    <section className={css.newsletter}>
      <div className={css.newsletterInner}>
        <span className={css.newsletterEyebrow}>📬 The Load Report</span>
        <h2 className={css.newsletterTitle}>Get Smarter About Hauling</h2>
        <p className={css.newsletterText}>
          New towing laws, rental tips, and no-BS trailer know-how — straight to your inbox. No
          spam, just the goods.
        </p>
        <form className={css.newsletterForm} onSubmit={onSubmit}>
          <input
            type="email"
            className={css.newsletterInput}
            placeholder="you@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            aria-label="Email address"
            disabled={status === 'loading' || status === 'ok'}
          />
          <button
            type="submit"
            className={css.newsletterBtn}
            disabled={status === 'loading' || status === 'ok'}
          >
            {status === 'loading' ? 'Signing up…' : status === 'ok' ? 'Subscribed ✓' : 'Sign Me Up'}
          </button>
        </form>
        {message ? (
          <p className={status === 'ok' ? css.newsletterOk : css.newsletterErr}>{message}</p>
        ) : null}
      </div>
    </section>
  );
};

const { bool } = PropTypes;

BlogPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const BlogPage = compose(
  withViewport,
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(BlogPageComponent);

export default BlogPage;
